import type { ClassValue } from 'clsx';
import type { MouseEvent } from 'react';

import { clsxm } from '@vs-shop/utils';
import { getCouponDiscountPercentage } from '@vs-shop/domain/price/getDiscountPercentageFromPrices';
import {
  ProductCouponAction,
  ProductCouponAmounts,
  ProductCouponCode,
  ProductCouponCodeVariant,
  ProductCouponPending,
  ProductCouponPrice,
  ProductCouponSaving,
} from '@vs-shop/commerce-ui/productCoupon';
import { MoonLoader } from 'react-spinners';

import { getDefaultCurrency } from '../../../config/currency';
import { purchaseButtonClassName } from '../../../config/purchaseButtonClass';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../utils/intl';

import { IntlMoney, IntlPercentage } from '../intl/Intl';

type Props = {
  /**
   * Whether the sentence spells the code out. The card has no room for it and
   * says only "Koupit s kódem"; the detail page has, and names it.
   *
   * Content and not an appearance: the shared mark paints the same face at both
   * mounts, and what the line SAYS is this tenant's copy.
   */
  showCodeName: boolean;
  price: number;
  priceWithCode: number;
  codeName: string;
  onUse: (event: MouseEvent<HTMLButtonElement>) => void;
  isLoading: boolean;
  className?: ClassValue;
};

/**
 * rcprofi's binding of the shared coupon mark's acting face — the same badge as
 * {@link ProductCouponStatic}, spending the code on click.
 *
 * `purchaseButtonClassName` is this tenant's purchase-tracking hook and rides
 * the class prop rather than the shared recipe: three other tenant controls
 * carry the same marker, and no other tenant has it.
 */
export default function ProductCouponClickable({
  showCodeName,
  price,
  priceWithCode,
  codeName,
  onUse,
  isLoading,
  className,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  const currency = getDefaultCurrency();

  const discountPercentage = getCouponDiscountPercentage(priceWithCode, price);

  return (
    <ProductCouponAction
      onClick={onUse}
      disabled={isLoading}
      className={clsxm(className, purchaseButtonClassName)}
    >
      {isLoading ? (
        <ProductCouponPending>
          <MoonLoader size={16} />
        </ProductCouponPending>
      ) : (
        <>
          <ProductCouponAmounts>
            {/* PERCENTAGE */}
            <ProductCouponSaving>
              &#8208;
              <IntlPercentage value={String(discountPercentage)} />
            </ProductCouponSaving>

            {/* PRICE */}
            <ProductCouponPrice>
              <IntlMoney value={{ amount: priceWithCode, currency: currency }} />
            </ProductCouponPrice>
          </ProductCouponAmounts>

          {/* CODE */}
          <ProductCouponCode variant={ProductCouponCodeVariant.UNDERLINED}>
            {intl.formatMessage(
              {
                id: 'product.productDiscountBanner.code',
                defaultMessage: 'Koupit s kódem {code}',
              },
              {
                code: showCodeName ? (
                  <span key="code" className="font-semibold">
                    {codeName}
                  </span>
                ) : null,
              }
            )}
          </ProductCouponCode>
        </>
      )}
    </ProductCouponAction>
  );
}
